import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import FolderIcon from '@mui/icons-material/Folder';
import usePermissions from '../../hooks/usePermissions';

/**
 * AddChildPermissionModal component
 * Modal for creating a new child permission under a parent
 */
const AddChildPermissionModal = ({ open, parentPermission, category, onClose, onSuccess }) => {
  const { createPermission, validatePath, loading } = usePermissions();

  const [formData, setFormData] = useState({
    name: '',
    pathSuffix: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [pathValidation, setPathValidation] = useState({ checking: false, result: null });
  const [submitError, setSubmitError] = useState(null);

  const fullPath = parentPermission ? `${parentPermission.path}.${formData.pathSuffix}` : '';

  // Reset form when modal opens/closes or parent changes
  useEffect(() => {
    if (open) {
      setFormData({ name: '', pathSuffix: '', description: '' });
      setErrors({});
      setPathValidation({ checking: false, result: null });
      setSubmitError(null);
    }
  }, [open, parentPermission]);

  // Validate path in real-time (debounced)
  useEffect(() => {
    if (!formData.pathSuffix || formData.pathSuffix.length < 1) {
      setPathValidation({ checking: false, result: null });
      return;
    }

    if (!parentPermission) {
      return;
    }

    const timer = setTimeout(async () => {
      setPathValidation({ checking: true, result: null });
      try {
        const result = await validatePath(fullPath);
        setPathValidation({ checking: false, result });
      } catch (err) {
        setPathValidation({ checking: false, result: null });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [fullPath, formData.pathSuffix, parentPermission, validatePath]);

  // Handle form field changes
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    setErrors(prev => ({ ...prev, [field]: '' }));
    setSubmitError(null);
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.pathSuffix.trim()) {
      newErrors.pathSuffix = 'Path suffix is required';
    } else if (!/^[a-z][a-z0-9_]*$/.test(formData.pathSuffix)) {
      newErrors.pathSuffix = 'Path suffix must follow format: lowercase letters, numbers, underscores only. Must start with a letter.';
    }

    if (pathValidation.result && !pathValidation.result.available) {
      newErrors.pathSuffix = pathValidation.result.message;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    try {
      setSubmitError(null);
      await createPermission({
        name: formData.name,
        path: fullPath,
        description: formData.description,
        category
      });
      onSuccess();
    } catch (err) {
      setSubmitError(err.message || err || 'Failed to create permission');
    }
  };

  // Get path input end adornment (icon/spinner)
  const getPathEndAdornment = () => {
    if (pathValidation.checking) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={20} />
        </InputAdornment>
      );
    }

    if (pathValidation.result) {
      if (pathValidation.result.available) {
        return (
          <InputAdornment position="end">
            <CheckCircleIcon color="success" />
          </InputAdornment>
        );
      } else {
        return (
          <InputAdornment position="end">
            <ErrorIcon color="error" />
          </InputAdornment>
        );
      }
    }

    return null;
  };

  // Get TextField color based on validation
  const getPathColor = () => {
    if (pathValidation.result) {
      return pathValidation.result.available ? 'success' : 'error';
    }
    return undefined;
  };

  if (!parentPermission) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add Child Permission
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          Category: {category === 'system' ? 'System' : 'Project'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {/* Parent Permission Info */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FolderIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">Parent Permission</Typography>
          </Box>
          <Typography variant="body2" fontWeight="medium">
            {parentPermission.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {parentPermission.path}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Name Field */}
          <TextField
            label="Permission Name"
            fullWidth
            required
            value={formData.name}
            onChange={handleChange('name')}
            error={Boolean(errors.name)}
            helperText={errors.name || 'Display name for the permission'}
            placeholder="e.g., Create, Edit, Delete, View"
          />

          {/* Path Suffix Field */}
          <Box>
            <TextField
              label="Path Suffix"
              fullWidth
              required
              value={formData.pathSuffix}
              onChange={handleChange('pathSuffix')}
              error={Boolean(errors.pathSuffix)}
              helperText={
                errors.pathSuffix ||
                pathValidation.result?.message ||
                'The unique identifier to append to parent path'
              }
              placeholder="e.g., create, edit, delete, view"
              color={getPathColor()}
              InputProps={{
                endAdornment: getPathEndAdornment()
              }}
              sx={{ fontFamily: 'monospace' }}
            />
            {/* Full Path Preview */}
            {formData.pathSuffix && (
              <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Full Path:
                </Typography>
                <Chip
                  label={fullPath}
                  size="small"
                  sx={{ fontFamily: 'monospace' }}
                />
              </Box>
            )}
          </Box>

          {/* Description Field */}
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={handleChange('description')}
            error={Boolean(errors.description)}
            helperText={errors.description || 'Optional description of what this permission controls'}
            placeholder="Describe what this permission allows users to do..."
          />

          {/* Path Format Help */}
          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="caption" component="div">
              <strong>Path Suffix Rules:</strong>
            </Typography>
            <Typography variant="caption" component="ul" sx={{ mt: 0.5, pl: 2 }}>
              <li>Must start with a lowercase letter</li>
              <li>Can contain lowercase letters, numbers, and underscores</li>
              <li>No dots allowed (dots separate hierarchy levels)</li>
              <li>Cannot be changed after creation</li>
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            pathValidation.checking ||
            (pathValidation.result && !pathValidation.result.available)
          }
        >
          {loading ? 'Creating...' : 'Create Permission'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddChildPermissionModal;
